'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback } from 'react'
import { Link2 } from 'lucide-react'
import { toast } from '@kiao-voice/ui-kit'
import { useTranslation } from 'react-i18next'

/**
 * Copying the link straight to the clipboard, with no share-sheet detour —
 * the row already offers Facebook/X/LINE as the sharing options next to it.
 * Takes the article's own canonical `url` rather than reading
 * `window.location.href`, so a visit carrying `?fbclid=`/`?utm_*=` never
 * leaks those params into what gets shared.
 */
function useCopyArticleLink(url: string): () => Promise<void> {
  const { t } = useTranslation('web')
  return useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success(t('blog.shareCopied'))
    } catch (caughtError) {
      reportHandledException(caughtError, { source: 'features/blog/share-controls.tsx' })
      toast.error(t('blog.shareFailed'))
    }
  }, [url, t])
}

const ICON_CLASS =
  'inline-flex size-9 shrink-0 -mx-[6px] items-center justify-center text-[var(--muted)] transition-colors hover:text-[var(--fg)] focus-visible:rounded-[3px] focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[var(--fg)]'

/**
 * Copy-link plus the three networks Taiwan readers actually forward articles
 * on. Shared verbatim between the header byline and the end-of-article row so
 * the label and icon set never drift between the two places it appears.
 *
 * Each icon sits in a 36×36 hit area (WCAG 2.5.8 minimum) even though the
 * glyph itself stays a bare 16–19px icon with no visible frame. `ICON_CLASS`
 * pulls each box in by 6px a side, so two adjacent boxes exactly touch
 * (`gap-3`'s 12px fully absorbed, zero overlap between hit areas) instead of
 * standing `gap-3` apart on top of their own invisible padding, which would
 * stretch the row noticeably wider than the reference image's rhythm.
 */
export function ShareArticleRow({ url }: { url: string }) {
  const { t } = useTranslation('web')
  const copyLink = useCopyArticleLink(url)
  const encoded = encodeURIComponent(url)
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-[length:var(--text-sm-plus)] text-[var(--muted)]">
        {t('blog.share')}
      </span>
      <span aria-hidden="true" className="h-4 w-px bg-[var(--border)]" />
      <button
        type="button"
        onClick={copyLink}
        aria-label={t('blog.copyLink')}
        className={ICON_CLASS}
      >
        <Link2 aria-hidden="true" className="size-[18px]" />
      </button>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encoded}`}
        target="_blank"
        rel="noreferrer noopener"
        aria-label={t('blog.shareFacebook')}
        className={ICON_CLASS}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="size-[19px]" fill="currentColor">
          <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z" />
        </svg>
      </a>
      <a
        href={`https://x.com/intent/post?url=${encoded}`}
        target="_blank"
        rel="noreferrer noopener"
        aria-label={t('blog.shareX')}
        className={ICON_CLASS}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="size-[16px]" fill="currentColor">
          <path d="M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z" />
        </svg>
      </a>
      <a
        href={`https://social-plugins.line.me/lineit/share?url=${encoded}`}
        target="_blank"
        rel="noreferrer noopener"
        aria-label={t('blog.shareLine')}
        className={ICON_CLASS}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="size-[19px]" fill="currentColor">
          <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
        </svg>
      </a>
    </div>
  )
}
